import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box, Button, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewBar from './ViewBar';
import VotingSystem from '../components/VotingSystem';
import { collection, getDocs, addDoc, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseConfig } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

// Function to get all marketing executions
const getMarketingExecutions = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'marketingExecution'));
    const executions: any[] = [];
    
    for (const docSnapshot of querySnapshot.docs) {
      const executionData = docSnapshot.data();
      
      // Try to get budget, goals, and duration from dashboard subcollection
      try {
        const dashboardDoc = await getDoc(doc(db, 'marketingExecution', docSnapshot.id, 'dashboard', 'main'));
        if (dashboardDoc.exists()) {
          const dashboardData = dashboardDoc.data();
          executionData.goals = dashboardData.goals;
          // If duration exists in dashboard subcollection, use it (overrides root document)
          if (dashboardData.duration) {
            executionData.duration = dashboardData.duration;
          }
        }
      } catch (error) {
        console.log(`Could not fetch dashboard data for execution ${docSnapshot.id}:`, error);
      }

      // Try to get budget from plan subcollection (spending data)
      try {
        const planDoc = await getDoc(doc(db, 'marketingExecution', docSnapshot.id, 'plan', 'main'));
        if (planDoc.exists()) {
          const planSubData = planDoc.data();
          if (planSubData.phases) {
            // Calculate total budget from all phases and weeks
            let totalBudget = 0;
            planSubData.phases.forEach((phase: any) => {
              if (phase.weeks) {
                phase.weeks.forEach((week: any) => {
                  if (week.spending && week.spending.Total) {
                    totalBudget += week.spending.Total;
                  }
                });
              }
            });
            executionData.budget = totalBudget;
          }
        }
      } catch (error) {
        console.log(`Could not fetch plan data for execution ${docSnapshot.id}:`, error);
      }
      
      executions.push({
        id: docSnapshot.id,
        ...executionData
      });
    }
    
    return executions;
  } catch (error) {
    console.error('Error getting marketing executions:', error);
    return [];
  }
};

// Function to create a new marketing execution
const createNewMarketingExecution = async (executionName: string) => {
  try {
    // Create the main execution document
    const executionRef = await addDoc(collection(db, 'marketingExecution'), {
      name: executionName,
      createdAt: new Date(),
      updatedAt: new Date()
    });

    // Create subcollections with main documents
    const subcollections = ['dashboard', 'strategy', 'plan', 'influencers', 'roadmap', 'execution'];
    
    for (const subcollection of subcollections) {
      const mainDocRef = doc(db, 'marketingExecution', executionRef.id, subcollection, 'main');
      const subcollectionData: any = {
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Add duration to dashboard subcollection so it shows up in the dashboard view
      if (subcollection === 'dashboard') {
        subcollectionData.duration = {
          start: new Date(),
          end: new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000) // 6 months from now
        };
      }

      await setDoc(mainDocRef, subcollectionData);
    }

    return executionRef.id;
  } catch (error) {
    console.error('Error creating new marketing execution:', error);
    return null;
  }
};

// Function to delete a marketing execution
const deleteMarketingExecution = async (executionId: string) => {
  try {
    await deleteDoc(doc(db, 'marketingExecution', executionId));
    return true;
  } catch (error) {
    console.error('Error deleting marketing execution:', error);
    return false;
  }
};

interface AllMarketingExecutionsProps {
  onExecutionSelect: (executionId: string, executionName: string) => void;
}

const AllMarketingExecutions: React.FC<AllMarketingExecutionsProps> = ({ onExecutionSelect }) => {
  const [marketingExecutions, setMarketingExecutions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateExecution, setShowCreateExecution] = useState(false);
  const [newExecutionName, setNewExecutionName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [executionToDelete, setExecutionToDelete] = useState<{ id: string; name: string } | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'vertical'>('grid');

  const handleDeleteExecution = async () => {
    if (!executionToDelete) return;
    
    setLoading(true);
    const success = await deleteMarketingExecution(executionToDelete.id);
    if (success) {
      await loadMarketingExecutions(); // Reload executions
    }
    setDeleteDialogOpen(false);
    setExecutionToDelete(null);
    setLoading(false);
  };

  const openDeleteDialog = (executionId: string, executionName: string) => {
    setExecutionToDelete({ id: executionId, name: executionName });
    setDeleteDialogOpen(true);
  };

  const handleCreateExecution = async () => {
    if (!newExecutionName.trim()) return;
    
    setLoading(true);
    const executionId = await createNewMarketingExecution(newExecutionName);
    if (executionId) {
      setNewExecutionName('');
      setShowCreateExecution(false);
      await loadMarketingExecutions(); // Reload executions
      onExecutionSelect(executionId, newExecutionName); // Select the new execution
    }
    setLoading(false);
  };

  // Load marketing executions
  const loadMarketingExecutions = async () => {
    try {
      const executions = await getMarketingExecutions();
      console.log('Loaded executions:', executions);
      setMarketingExecutions(executions);
    } catch (error) {
      console.error('Error loading marketing executions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load data from Firebase on component mount
  useEffect(() => {
    loadMarketingExecutions();
  }, []);

  const handleExecutionSelect = (executionId: string, executionName: string) => {
    console.log('Execution selected:', executionId, executionName);
    const displayName = executionName || `Execution ${executionId}`;
    onExecutionSelect(executionId, displayName);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in progress': return 'warning';
      case 'open': return 'default';
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-gray-100 text-gray-800 min-h-screen flex items-center justify-center">
        <Typography variant="h6">Loading marketing executions...</Typography>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 text-gray-800 min-h-screen">
      <ViewBar 
        view={viewMode} 
        onViewChange={setViewMode} 
        onCreateNew={() => setShowCreateExecution(true)}
        createButtonText="Create New Execution"
      />
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8" style={{ marginTop: '60px' }}>
        <Grid container spacing={3} sx={{
          ...(viewMode === 'vertical' && {
            flexDirection: 'column',
            maxWidth: '800px',
            margin: '0 auto',
            '& .MuiGrid-item': {
              maxWidth: '100%',
              flexBasis: '100%'
            }
          })
        }}>
          {marketingExecutions.map((execution) => (
            <Grid item xs={12} sm={6} md={4} key={execution.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => handleExecutionSelect(execution.id, execution.name)}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', flex: 1 }}>
                      {execution.name || `Execution ${execution.id}`}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VotingSystem 
                        collectionName="marketingExecution" 
                        documentId={execution.id} 
                        size="small"
                        showCounts={true}
                        showTooltips={true}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteDialog(execution.id, execution.name);
                        }}
                        sx={{ 
                          color: 'error.main',
                          '&:hover': { backgroundColor: 'error.light', color: 'white' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Duration
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {execution.duration?.start?.toDate?.()?.toLocaleDateString() || 'Not set'} - {execution.duration?.end?.toDate?.()?.toLocaleDateString() || 'Not set'}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Budget
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatCurrency(execution.budget || 0)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Goals
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {execution.goals?.newInstallsGoal ? `${execution.goals.newInstallsGoal.toLocaleString()} new installs` : 'Not set'}
                    </Typography>
                  </Box>

                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {marketingExecutions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Marketing Executions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first marketing execution to get started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateExecution(true)}
              sx={{ backgroundColor: '#4f46e5', '&:hover': { backgroundColor: '#4338ca' } }}
            >
              Create First Execution
            </Button>
          </Box>
        )}
      </div>

      {/* Create Execution Dialog */}
      <Dialog open={showCreateExecution} onClose={() => setShowCreateExecution(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Marketing Execution</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Execution Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newExecutionName}
            onChange={(e) => setNewExecutionName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateExecution();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateExecution(false)}>Cancel</Button>
          <Button onClick={handleCreateExecution} variant="contained" disabled={!newExecutionName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Marketing Execution</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{executionToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteExecution} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AllMarketingExecutions; 